import { getDb, withDbWrite } from './database';
import { isHttpStorageAvailable } from './persistence';
import { createPasswordRecord, verifyPassword } from './password';

const SESSION_KEY = 'workschedule-session';

export type AuthUser = {
  username: string;
};

export type AuthErrorCode =
  | 'invalidCredentials'
  | 'currentPasswordIncorrect'
  | 'passwordTooShort'
  | 'passwordMismatch'
  | 'passwordUnchanged'
  | 'authError';

export class AuthError extends Error {
  code: AuthErrorCode;

  constructor(code: AuthErrorCode, message = code) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

type UserRow = {
  id: string;
  username: string;
  password_salt: string;
  password_hash: string;
};

const normalizeUsername = (username: string) => username.trim();

const readLocalSession = (): AuthUser | null => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { username?: string };
    if (typeof parsed.username !== 'string' || !parsed.username) return null;
    return { username: parsed.username };
  } catch {
    return null;
  }
};

const writeLocalSession = (user: AuthUser | null) => {
  if (!user) {
    window.sessionStorage.removeItem(SESSION_KEY);
    return;
  }
  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(user));
};

const parseAuthResponse = async (response: Response): Promise<AuthUser> => {
  if (response.status === 401) throw new AuthError('invalidCredentials');
  if (!response.ok) throw new AuthError('authError');

  const body = (await response.json()) as { user?: AuthUser };
  if (!body.user?.username) throw new AuthError('authError');
  return body.user;
};

const findUser = async (username: string): Promise<UserRow | null> => {
  const db = await getDb();
  const statement = db.prepare(
    'SELECT id, username, password_salt, password_hash FROM users WHERE username = ? COLLATE NOCASE',
  );
  statement.bind([username]);
  const row = statement.step()
    ? (statement.getAsObject() as UserRow)
    : null;
  statement.free();
  return row;
};

const HTTP_STORAGE_HEADER = 'x-work-schedule-storage';

const readRemoteAuthStatus = async () => {
  const response = await fetch('/api/auth/status', { cache: 'no-store' });
  if (!response.ok || response.headers.get(HTTP_STORAGE_HEADER) !== 'sqlite') {
    return null;
  }

  const body = (await response.json()) as { user?: AuthUser | null };
  return {
    user: body.user?.username ? { username: body.user.username } : null,
  };
};

export const getAuthStatus = async (): Promise<{ user: AuthUser | null }> => {
  try {
    const remote = await readRemoteAuthStatus();
    if (remote) return remote;
  } catch {
    // Fall through to the local sqlite user table.
  }

  const stored = readLocalSession();
  if (!stored) return { user: null };

  const existing = await findUser(stored.username);
  if (!existing) {
    writeLocalSession(null);
    return { user: null };
  }

  return { user: { username: existing.username } };
};

export const login = async (
  username: string,
  password: string,
): Promise<AuthUser> => {
  const normalized = normalizeUsername(username);
  if (!normalized || !password) {
    throw new AuthError('invalidCredentials');
  }

  if (await isHttpStorageAvailable()) {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: normalized, password }),
    });
    return parseAuthResponse(response);
  }

  const row = await findUser(normalized);
  if (
    !row ||
    !(await verifyPassword(password, row.password_salt, row.password_hash))
  ) {
    throw new AuthError('invalidCredentials');
  }

  const user = { username: row.username };
  writeLocalSession(user);
  return user;
};

export const logout = async () => {
  writeLocalSession(null);
  if (await isHttpStorageAvailable()) {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
  }
};

const MIN_PASSWORD_LENGTH = 8;

export const changePassword = async (
  username: string,
  currentPassword: string,
  newPassword: string,
) => {
  if (!username || !currentPassword || !newPassword) {
    throw new AuthError('authError');
  }
  if (newPassword.length < MIN_PASSWORD_LENGTH) {
    throw new AuthError('passwordTooShort');
  }
  if (currentPassword === newPassword) {
    throw new AuthError('passwordUnchanged');
  }

  const row = await findUser(username);
  if (
    !row ||
    !(await verifyPassword(currentPassword, row.password_salt, row.password_hash))
  ) {
    throw new AuthError('currentPasswordIncorrect');
  }

  const { salt, hash } = await createPasswordRecord(newPassword);
  await withDbWrite((db) => {
    db.run(
      `UPDATE users SET password_salt = ?, password_hash = ? WHERE username = ? COLLATE NOCASE`,
      [salt, hash, username],
    );
  });
};
